import React from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'

// Layout Components
import MainLayout from './components/common/Layout/MainLayout.jsx'

// Auth Pages
import SuperAdminLogin from './pages/Auth/SuperAdminLogin.jsx'
import OrgAdminLogin from './pages/Auth/OrgAdminLogin.jsx'
import EmployeeLogin from './pages/Auth/EmployeeLogin.jsx'
import ResetPassword from './pages/Auth/ResetPassword.jsx'
import ForgotPassword from './pages/Auth/ForgotPassword.jsx'
import ChangePassword from './pages/Auth/ChangePassword.jsx'
import DeviceLogin from './pages/Auth/DeviceLogin.jsx'

// Super Admin Pages
import SuperAdminDashboard from './pages/SuperAdmin/Dashboard.jsx'
import Organizations from './pages/SuperAdmin/Organizations.jsx'
import OrganizationDetails from './pages/SuperAdmin/OrganizationDetails.jsx'
import UsersManagement from './pages/SuperAdmin/UsersManagement.jsx'
import Devices from './pages/SuperAdmin/Devices.jsx'
import AuditLogs from './pages/SuperAdmin/AuditLogs.jsx'

// Org Admin Pages
import OrgDashboard from './pages/OrgAdmin/Dashboard.jsx'
import Employees from './pages/OrgAdmin/Employees.jsx'
import AddEmployee from './pages/OrgAdmin/AddEmployee.jsx'
import EmployeeDetails from './pages/OrgAdmin/EmployeeDetails.jsx'
import FaceEnrollment from './pages/OrgAdmin/FaceEnrollment.jsx'
import Attendance from './pages/OrgAdmin/Attendance.jsx'
import AttendanceSummary from './pages/OrgAdmin/AttendanceSummary.jsx'
import Shifts from './pages/OrgAdmin/Shifts.jsx'
import ShiftAssignments from './pages/OrgAdmin/ShiftAssignments.jsx'
import PayrollDashboard from './pages/OrgAdmin/Payroll/PayrollDashboard.jsx'
import PayslipPage from './pages/OrgAdmin/Payroll/PayslipPage.jsx'
import PayrollHistoryPage from './pages/OrgAdmin/Payroll/PayrollHistoryPage.jsx'
import OrgDevices from './pages/OrgAdmin/OrgDevices.jsx'
import Settings from './pages/OrgAdmin/Settings.jsx'
import EditEmployee from './pages/OrgAdmin/EditEmployee.jsx'
import EmployeeLeaveManagement from './pages/OrgAdmin/EmployeeLeaveManagement.jsx'
import HolidayManagement from './pages/OrgAdmin/HolidayManagement.jsx'

// Employee Pages
import EmployeeDashboard from './pages/Employee/Dashboard.jsx'
import EmployeeAttendance from './pages/Employee/Attendance.jsx'
import LeaveManagement from './pages/Employee/LeaveManagement.jsx'
import ApplyLeave from './pages/Employee/ApplyLeave.jsx'
import LeaveHistory from './pages/Employee/LeaveHistory.jsx'
import EmployeeProfile from './pages/Employee/Profile.jsx'
import EmployeeSettings from './pages/Employee/Settings.jsx'
import AttendanceHistory from './pages/Employee/AttendanceHistory.jsx'
import TestFaceAttendance from './pages/Employee/TestFaceAttendance.jsx'
import HolidayList from './pages/Employee/HolidayList.jsx'

// Common Components
import Loader from './components/common/UI/Loader.jsx'

// Protected Route Component
import ProtectedRoute from './components/common/Navigation/ProtectedRoute.jsx'

// Import Box and Typography for PlaceholderPage
import { Box, Typography } from '@mui/material'
import Kiosk from './pages/Device/Kiosk.jsx'

const AppRoutes = () => {
  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            fontSize: '14px',
            maxWidth: '500px',
          },
        }}
      />
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Navigate to="/login" />} />
          <Route path="/super-admin/login" element={<SuperAdminLogin />} />
          <Route path="/org/login" element={<OrgAdminLogin />} />
          <Route path="/login" element={<EmployeeLogin />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route
            path="/reset-password"
            element={
              <ProtectedRoute allowedRoles={['admin', 'hr', 'superadmin']}>
                <ResetPassword />
              </ProtectedRoute>
            }
          />

          {/* Test Page for FaceIO */}
          <Route path="/employee/test-face-io" element={<TestFaceAttendance />} />

          {/* Super Admin Routes */}
          <Route
            path="/super-admin"
            element={
              <ProtectedRoute allowedRoles={['superadmin']}>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="dashboard" />} />
            <Route path="dashboard" element={<SuperAdminDashboard />} />
            <Route path="organizations" element={<Organizations />} />
            <Route path="organizations/:id" element={<OrganizationDetails />} />
            <Route path="users" element={<UsersManagement />} />
            <Route path="devices" element={<Devices />} />
            {/* <Route path="audit-logs" element={<AuditLogs />} /> */}
          </Route>

          {/* Organization Admin Routes */}
          <Route
            path="/org"
            element={
              <ProtectedRoute allowedRoles={['admin', 'hr']}>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="dashboard" />} />
            <Route path="dashboard" element={<OrgDashboard />} />
            <Route path="employees" element={<Employees />} />
            <Route path="employees/add" element={<AddEmployee />} />
            <Route path="/org/employees/:id/edit" element={<EditEmployee />} />
            <Route path="employees/:id" element={<EmployeeDetails />} />
            <Route path="attendance" element={<Attendance />} />
            <Route path="attendance/summary" element={<AttendanceSummary />} />
            <Route path="/org/leaves" element={<EmployeeLeaveManagement />} />
            <Route path="holidays" element={<HolidayManagement />} />
            <Route path="shifts" element={<Shifts />} />
            <Route path="shifts/assignments" element={<ShiftAssignments />} />
            <Route path="shifts/:id" element={<ShiftAssignments />} />
            <Route path="payroll" element={<PayrollDashboard />} />
            <Route path="payroll/payslip/:id" element={<PayslipPage />} />
            <Route path="payroll/history/:employeeId" element={<PayrollHistoryPage />} />
            <Route path="devices" element={<OrgDevices />} />
            <Route path="settings" element={<Settings />} />
          </Route>

          {/* Standalone Face Enrollment (no sidebar) */}
          <Route
            path="/org/employees/:id/enroll"
            element={
              <ProtectedRoute allowedRoles={['admin', 'hr']}>
                <FaceEnrollment />
              </ProtectedRoute>
            }
          />

          {/* Change Password Page (Protected, No Layout) */}
          <Route
            path="/change-password"
            element={
              <ProtectedRoute allowedRoles={['employee']}>
                <ChangePassword />
              </ProtectedRoute>
            }
          />


          {/* Device Routes */}
          <Route path="/device/login" element={<DeviceLogin />} />
          <Route path="/device/kiosk" element={<Kiosk />} />

          {/* Employee Routes */}
          <Route
            path="/employee"
            element={
              <ProtectedRoute allowedRoles={['employee']}>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="dashboard" />} />
            <Route path="dashboard" element={<EmployeeDashboard />} />
            <Route path="attendance" element={<EmployeeAttendance />} />
            <Route path="attendance/history" element={<AttendanceHistory />} />
            <Route path="leave" element={<LeaveManagement />} />
            <Route path="leave/apply" element={<ApplyLeave />} />
            <Route path="leave/history" element={<LeaveHistory />} />
            <Route path="holidays" element={<HolidayList />} />
            <Route path="profile" element={<EmployeeProfile />} />
            <Route path="settings" element={<EmployeeSettings />} />

          </Route>

          {/* 404 Route */}
          <Route path="*" element={
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
              flexDirection: 'column',
              gap: '20px'
            }}>
              <h1>404 - Page Not Found</h1>
              <p>The page you are looking for does not exist.</p>
              <a href="/">Go to Home</a>
            </div>
          } />
        </Routes>
      </BrowserRouter>
    </>
  )
}

export default AppRoutes